import { isEmpty } from "lodash";
import React, { useCallback, useState } from "react";
import { useCommissionReports } from ".";
import LoadingIcon from "../../components/icons/LoadingIcon";
import { CurrencyKey } from "../../utils/useCurrency";
import { useContacts } from "../contacts";
import { ContactPaymentContextKey } from "../contacts/ContactsProvider";
import { useToast } from "../toast";

interface Props {
  onSuccess: () => void;
  commissionReportUuid: string;
  contactPaymentValues: {
    contact_uuid: string;
    currency: CurrencyKey;
    amount: number;
    context: ContactPaymentContextKey;
  }[];
}

const MarkAsPaidButton: React.FC<Props> = ({
  onSuccess,
  commissionReportUuid,
  contactPaymentValues
}) => {
  const { addToast } = useToast();
  const { addContactPayments } = useContacts();
  const { fetchCommissionReportById } = useCommissionReports();
  const [requestStatus, setRequestStatus] = useState<
    "idle" | "submitting" | "completed"
  >("idle");

  const markAsPaid = useCallback(async () => {
    setRequestStatus("submitting");

    const response = await addContactPayments(
      commissionReportUuid,
      contactPaymentValues
    );

    if (response?.status) {
      await fetchCommissionReportById(commissionReportUuid);

      setRequestStatus("completed");

      addToast({
        type: "success",
        title: "Successfully marked as paid"
      });

      onSuccess();
    } else {
      addToast({
        type: "error",
        title: "Something went wrong"
      });

      setRequestStatus("completed");
    }
  }, [
    addContactPayments,
    commissionReportUuid,
    contactPaymentValues,
    fetchCommissionReportById,
    addToast,
    onSuccess
  ]);

  return (
    <button
      className="flex items-center ml-4 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-xs md:text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-500 dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-900 dark:border-gray-700 dark:hover:border-gray-400 disabled:opacity-50 disabled:cursor-default dark:disabled:hover:border-gray-700 dark:disabled:hover:bg-gray-900 dark:disabled:hover:text-gray-400"
      onClick={markAsPaid}
      disabled={requestStatus === "submitting" || isEmpty(contactPaymentValues)}
    >
      {requestStatus === "submitting" && (
        <LoadingIcon className="animate-spin -ml-1 mr-3 h-5 w-5" />
      )}
      <span>
        {requestStatus === "submitting" ? "Processing" : "Mark as paid"}
      </span>
    </button>
  );
};

export default MarkAsPaidButton;
